/**
 * Orchestration for the merchant onboarding flow.
 *
 * The server owns the position (which step, how much demo allowance is left,
 * whether activation is verified); this hook owns nothing but the calls and
 * the transient browser-side artefacts — the photo preview and the generated
 * result — which by design never reach a server or an analytics event.
 *
 * The last successful demo result is kept in sessionStorage so a reload on
 * the same device can show it again ("keep the merchant result where
 * privacy-safe on that device"). It is an image of the merchant themself, so
 * it stays session-local: no cookie, no server copy, gone when the browser
 * session ends.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import type {
  ActivationCheckData,
  ApiResponse,
  DemoError,
  DemoSuccessData,
  OnboardingProductsData,
  OnboardingStateData,
  OnboardingFlowStep,
} from '../types';
import { dataUrlToBlob } from './imagePrep';

const RESULT_STORAGE_KEY = 'aisthetix-onboarding-result';

export interface StoredDemoResult {
  image: string;
  product: DemoSuccessData['product'];
  createdAt: string;
}

export function readStoredResult(): StoredDemoResult | null {
  try {
    const raw = sessionStorage.getItem(RESULT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDemoResult;
    return typeof parsed?.image === 'string' && parsed.image.startsWith('data:image/') ? parsed : null;
  } catch {
    return null;
  }
}

function storeResult(result: StoredDemoResult): void {
  try {
    sessionStorage.setItem(RESULT_STORAGE_KEY, JSON.stringify(result));
  } catch {
    // Quota or privacy mode: the result simply does not survive a reload.
  }
}

export interface OnboardingPlan {
  planTier: string;
  status: string;
}

export type DemoOutcome =
  | { ok: true; data: DemoSuccessData }
  | { ok: false; error: DemoError };

export interface UseOnboardingResult {
  state: OnboardingStateData | null;
  plan: OnboardingPlan | null;
  loading: boolean;
  /** True when the state endpoint itself failed — the caller falls back to
   * the classic dashboard rather than trapping the merchant in a broken flow. */
  failed: boolean;
  products: OnboardingProductsData | null;
  productsLoading: boolean;
  storedResult: StoredDemoResult | null;
  refresh: () => Promise<void>;
  loadProducts: () => Promise<void>;
  selectProduct: (productId: string) => Promise<void>;
  advance: (to: OnboardingFlowStep) => Promise<void>;
  runDemo: (photoDataUrl: string, productId: string) => Promise<DemoOutcome>;
  checkActivation: () => Promise<ActivationCheckData | null>;
  enter: () => Promise<void>;
  dismiss: () => Promise<void>;
}

const UNEXPECTED: DemoError = { reason: 'unexpected', retryable: true, switchProduct: false };

export function useOnboarding(): UseOnboardingResult {
  const authenticatedFetch = useAuthenticatedFetch();
  const [state, setState] = useState<OnboardingStateData | null>(null);
  const [plan, setPlan] = useState<OnboardingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [products, setProducts] = useState<OnboardingProductsData | null>(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [storedResult, setStoredResult] = useState<StoredDemoResult | null>(readStoredResult);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/onboarding/state');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json: ApiResponse<{ state: OnboardingStateData; plan: OnboardingPlan | null }> =
        await response.json();
      if (!json.success || !json.data) throw new Error(json.error || 'state failed');
      if (!mounted.current) return;
      setState(json.data.state);
      setPlan(json.data.plan);
      setFailed(false);
    } catch (err) {
      console.error('Onboarding state error:', err);
      if (mounted.current) setFailed(true);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const response = await authenticatedFetch('/api/onboarding/products');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json: ApiResponse<OnboardingProductsData> = await response.json();
      if (json.success && json.data && mounted.current) setProducts(json.data);
    } catch (err) {
      console.error('Onboarding products error:', err);
      if (mounted.current) {
        setProducts({ products: [], autoSelectedId: null, selectedId: null, riskyOnly: false });
      }
    } finally {
      if (mounted.current) setProductsLoading(false);
    }
  }, [authenticatedFetch]);

  const selectProduct = useCallback(
    async (productId: string) => {
      setProducts((prev) => (prev ? { ...prev, selectedId: productId } : prev));
      setState((prev) => (prev ? { ...prev, selectedProductId: productId } : prev));
      try {
        await authenticatedFetch('/api/onboarding/select-product', {
          method: 'POST',
          body: JSON.stringify({ productId }),
        });
      } catch (err) {
        console.error('Onboarding select error:', err);
      }
    },
    [authenticatedFetch]
  );

  const advance = useCallback(
    async (to: OnboardingFlowStep) => {
      try {
        const response = await authenticatedFetch('/api/onboarding/advance', {
          method: 'POST',
          body: JSON.stringify({ to }),
        });
        const json: ApiResponse<{ state: OnboardingStateData }> = await response.json();
        if (json.success && json.data && mounted.current) setState(json.data.state);
      } catch (err) {
        console.error('Onboarding advance error:', err);
      }
    },
    [authenticatedFetch]
  );

  const runDemo = useCallback(
    async (photoDataUrl: string, productId: string): Promise<DemoOutcome> => {
      try {
        const form = new FormData();
        form.append('photo', dataUrlToBlob(photoDataUrl), 'photo.jpg');
        form.append('productId', productId);
        const response = await authenticatedFetch('/api/onboarding/demo', {
          method: 'POST',
          body: form,
        });
        const json = (await response.json().catch(() => null)) as
          | { success?: boolean; data?: DemoSuccessData; error?: DemoError }
          | null;
        if (response.ok && json?.success && json.data) {
          // The engine's format is provider-dependent (a live run returned
          // JPEG); the server sniffs the real one from the bytes.
          const mimeType = json.data.imageMimeType ?? 'image/png';
          const result: StoredDemoResult = {
            image: `data:${mimeType};base64,${json.data.image}`,
            product: json.data.product,
            createdAt: new Date().toISOString(),
          };
          storeResult(result);
          if (mounted.current) {
            setStoredResult(result);
            setState((prev) =>
              prev
                ? {
                    ...prev,
                    step: 'result',
                    demo: json.data!.demo ?? prev.demo,
                  }
                : prev
            );
          }
          return { ok: true, data: json.data };
        }
        const error = json?.error && typeof json.error === 'object' ? json.error : UNEXPECTED;
        return { ok: false, error };
      } catch (err) {
        console.error('Onboarding demo error:', err);
        return { ok: false, error: { reason: 'engine_unavailable', retryable: true, switchProduct: false } };
      }
    },
    [authenticatedFetch]
  );

  const checkActivation = useCallback(async (): Promise<ActivationCheckData | null> => {
    try {
      const response = await authenticatedFetch('/api/onboarding/activation');
      const json: ApiResponse<ActivationCheckData> = await response.json();
      if (json.success && json.data) {
        if (json.data.state && mounted.current) setState(json.data.state);
        return json.data;
      }
      return null;
    } catch (err) {
      console.error('Onboarding activation check error:', err);
      return null;
    }
  }, [authenticatedFetch]);

  const enter = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/onboarding/enter', { method: 'POST' });
      const json: ApiResponse<{ state: OnboardingStateData }> = await response.json();
      if (json.success && json.data && mounted.current) setState(json.data.state);
    } catch (err) {
      console.error('Onboarding enter error:', err);
    }
  }, [authenticatedFetch]);

  const dismiss = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/onboarding/dismiss', { method: 'POST' });
      const json: ApiResponse<{ state: OnboardingStateData }> = await response.json();
      if (json.success && json.data && mounted.current) setState(json.data.state);
    } catch (err) {
      console.error('Onboarding dismiss error:', err);
    }
  }, [authenticatedFetch]);

  return {
    state,
    plan,
    loading,
    failed,
    products,
    productsLoading,
    storedResult,
    refresh,
    loadProducts,
    selectProduct,
    advance,
    runDemo,
    checkActivation,
    enter,
    dismiss,
  };
}
